var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix3, Matrix4, Mesh, Object3D, Quaternion, Vector3 } from "three";
import { assert, readBatchTable, readFeatureTable } from "../../../utils";
import { readFeatureTableObject } from "./readTables";
import { FileLoader } from "../../../data/loaders";
import { Ellipsoid, MathUtils } from "../../../math";
import { readBatchModel } from "../Batch3DModel/readBatchModel";
import { Batch3DModel } from "..";
import { ObjectType } from "../../../picking";
import { OptionsManager } from "../../../options";
export class Instanced3DModel extends Object3D {
    constructor(featureTable, materialRegistry, pickingManager, objectId, batchTable) {
        super();
        this.tileCost = OptionsManager.INSTANCED_MODEL_TILE_WEIGHT;
        this._featureTable = featureTable;
        this._batchTable = batchTable;
        this.materialRegistry = materialRegistry;
        this.pickingManager = pickingManager;
        this.objectId = objectId;
        this.styleCallbackFn = (v) => this.styleCallback(v);
        this.clippingPlaneCallbackFn = (v) => this.clippingPlaneCallback(v);
        this.invalidateCallbackFn = () => this.invalidateCallback();
        materialRegistry.styleCallbacks.add(this.styleCallbackFn);
        materialRegistry.clippingPlaneCallbacks.add(this.clippingPlaneCallbackFn);
        materialRegistry.invalidateCallbacks.add(this.invalidateCallbackFn);
    }
    static create(tile, data, styleDesc, materialRegistry, pickingManager, owner) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // View of array buffer for reading
            const view = data;
            // Generate a new ID for this object
            const objectId = pickingManager.newId();
            // Check if the file header is valid ("pnts"). If not then we aren't reading a pnts file and should throw
            assert(Instanced3DModel.isValidHeader(view));
            // We only support version 1, check the field in the header
            const version = view.getUint32(4, true);
            assert(version === 1);
            // Depending on this flag in the header the GLTF section either contains a URL to a gltf file or an embedded
            // glb file
            const gltfIsUri = view.getUint32(28, true) === 0;
            // Read the feature and batch table strings and data
            const { featureTableData, batchTableData, gltfSection } = this.loadFileSections(view);
            // Parse the feature and batch table
            const featureTable = readFeatureTableObject(featureTableData);
            const batchTable = {}; // readBatchTableObject(batchTableData);
            const castedTile = tile;
            const { baseUri, gltfBuffer } = yield this.loadGltfBuffer(gltfIsUri, gltfSection, castedTile);
            // The geometry will be loaded differently depending on what extensions the point cloud uses.
            const gltf = yield readBatchModel(gltfBuffer, `${baseUri}/../`, {
                objectId: objectId || 0,
                clippingPlanes: materialRegistry.clippingPlanes,
            });
            const object = new Instanced3DModel(featureTable, materialRegistry, pickingManager, objectId, batchTable);
            const upAxis = (_a = tile.extras.owner.asset.gltfUpAxis) !== null && _a !== void 0 ? _a : "Y";
            for (let i = 0; i < featureTable.instancesLength; i += 1) {
                // TODO: We end up with instancesLength + 1, we should avoid cloning the last scene and use it so it can
                //       be correctly disposed
                const clone = gltf.scene.clone(true);
                // Load the position
                clone.position.x = featureTable.positions[i * 3];
                clone.position.y = featureTable.positions[i * 3 + 1];
                clone.position.z = featureTable.positions[i * 3 + 2];
                // Load the rotation
                if (featureTable.rotation !== undefined) {
                    const upX = featureTable.rotation.normalUp[i * 3];
                    const upY = featureTable.rotation.normalUp[i * 3 + 1];
                    const upZ = featureTable.rotation.normalUp[i * 3 + 2];
                    const up = new Vector3(upX, upY, upZ);
                    const rightX = featureTable.rotation.normalRight[i * 3];
                    const rightY = featureTable.rotation.normalRight[i * 3 + 1];
                    const rightZ = featureTable.rotation.normalRight[i * 3 + 2];
                    const right = new Vector3(rightX, rightY, rightZ);
                    const forward = up.clone().cross(right);
                    // Construct a rotation matrix from the basis frame
                    const matrix3 = new Matrix3();
                    matrix3.elements[0] = right.x;
                    matrix3.elements[1] = right.y;
                    matrix3.elements[2] = right.z;
                    matrix3.elements[3] = up.x;
                    matrix3.elements[4] = up.y;
                    matrix3.elements[5] = up.z;
                    matrix3.elements[6] = forward.x;
                    matrix3.elements[7] = forward.y;
                    matrix3.elements[8] = forward.z;
                    const matrix4 = new Matrix4().setFromMatrix3(matrix3);
                    clone.quaternion.setFromRotationMatrix(matrix4);
                    switch (upAxis) {
                        case "X":
                            // TODO: this
                            break;
                        case "Y":
                            const quaternion = new Quaternion().setFromAxisAngle(MathUtils.X_AXIS, Math.PI / 2);
                            clone.quaternion.multiply(quaternion);
                            break;
                        case "Z":
                            break;
                    }
                }
                else if (featureTable.eastNorthUp) {
                    const cartographic = Ellipsoid.WGS_84.cartographicForPosition(clone.position);
                    const basis = Ellipsoid.WGS_84.rotationBasisForCartographic(cartographic, new Matrix3());
                    const basis4 = new Matrix4().setFromMatrix3(basis);
                    clone.quaternion.setFromRotationMatrix(basis4);
                }
                else {
                    switch (upAxis) {
                        case "X":
                            // TODO: this
                            break;
                        case "Y":
                            const quaternion = new Quaternion().setFromAxisAngle(MathUtils.X_AXIS, Math.PI / 2);
                            clone.quaternion.multiply(quaternion);
                            break;
                        case "Z":
                            break;
                    }
                }
                // Load the scale
                if (featureTable.scale !== undefined) {
                    clone.scale.multiplyScalar(featureTable.scale[i]);
                }
                if (featureTable.scaleNonUniform !== undefined) {
                    clone.scale.x *= featureTable.scaleNonUniform[i * 3];
                    clone.scale.y *= featureTable.scaleNonUniform[i * 3 + 1];
                    clone.scale.z *= featureTable.scaleNonUniform[i * 3 + 2];
                }
                object.add(clone);
            }
            // Apply the style definition for the first time
            object.styleCallback(styleDesc);
            // The GLTF2 loader clones the material so we need to force the children to share the same reference to the clipping planes
            object.traverse((v) => {
                var _a;
                if (v instanceof Mesh) {
                    const material = v.material;
                    material.clippingPlanes = (_a = materialRegistry.clippingPlanes) !== null && _a !== void 0 ? _a : [];
                    material.needsUpdate = true;
                }
            });
            Instanced3DModel.resolveTransforms(object, gltf, featureTable, tile);
            // Register the object into the picking manager
            pickingManager.setForId(object.objectId, {
                objectType: ObjectType.Composite,
                rootObject: owner,
                subObject: object,
            });
            pickingManager.setIdForObject(object.id, object.objectId);
            return object;
        });
    }
    static loadGltfBuffer(gltfIsUri, gltfSection, tile) {
        return __awaiter(this, void 0, void 0, function* () {
            let baseUri;
            let gltfBuffer;
            if (gltfIsUri) {
                // Decode the uri and load the GLTF file data
                const decoder = new TextDecoder();
                const gltfUri = decoder.decode(gltfSection);
                const gltfData = yield FileLoader.loadArrayBuffer(gltfUri);
                gltfBuffer = new DataView(gltfData);
                // If the binary data starts with a 'g' character then it can't be JSON and must be binary glb. If it
                // doesn't start with a 'g' we assume it's gltf json and decode the text to a string
                if (gltfBuffer.getUint8(0) !== 103) {
                    gltfBuffer = decoder.decode(gltfBuffer);
                }
                // The base uri for parsing the gltf will be where the gltf file was loaded from
                baseUri = gltfUri;
            }
            else {
                // Binary GLTF will use the internal buffer directly
                gltfBuffer = gltfSection;
                // We use the URL of the tile as the base in this case
                if (tile.content.uri !== undefined && tile.content.uri !== null) {
                    baseUri = tile.content.uri;
                }
                else if (tile.content.url !== undefined && tile.content.url !== null) {
                    baseUri = tile.content.url;
                }
                else {
                    throw new Error("Missing uri or url in content");
                }
            }
            return { baseUri, gltfBuffer };
        });
    }
    static loadFileSections(view) {
        const featureTableData = readFeatureTable(view, 32);
        const batchTableData = readBatchTable(view, 32);
        const gltfSection = readGltfSection(view);
        return { featureTableData, batchTableData, gltfSection };
    }
    static isValidHeader(view) {
        if (view.getUint8(0) !== 105)
            return false; // i
        if (view.getUint8(1) !== 51)
            return false; // 3
        if (view.getUint8(2) !== 100)
            return false; // d
        if (view.getUint8(3) !== 109)
            return false; // m
        return true;
    }
    static resolveTransforms(object, gltf, featureTable, tile) {
        // RTC_CENTER is applied first, so translate by the value it holds
        const translation = new Matrix4();
        if (typeof featureTable.rtcCenter !== "undefined") {
            translation.makeTranslation(featureTable.rtcCenter[0], featureTable.rtcCenter[1], featureTable.rtcCenter[2]);
        }
        else if (gltf.json !== undefined) {
            if (typeof gltf.json === "object" && gltf.json.extensionsUsed !== undefined) {
                const extensionsUsed = gltf.json.extensionsUsed;
                const used = extensionsUsed.indexOf("CESIUM_RTC") !== -1;
                if (used) {
                    const rtcCenter = gltf.json.extensions.CESIUM_RTC.center;
                    translation.makeTranslation(rtcCenter[0], rtcCenter[1], rtcCenter[2]);
                }
            }
        }
        // Lastly we recursively apply the parent transforms until we reach the root. localTransform holds the result
        // of doing this to the parent tile so we only need to do a single matrix multiply here
        const localTransform = tile.extras.transform;
        const final = localTransform.clone().invert();
        // Decompose the matrix into the object's position, rotation and scale as these are the authoritative source of
        // the object's transform
        final.decompose(object.position, object.quaternion, object.scale);
    }
    evict() {
        this.materialRegistry.styleCallbacks.delete(this.styleCallbackFn);
        this.materialRegistry.clippingPlaneCallbacks.delete(this.clippingPlaneCallbackFn);
        this.materialRegistry.invalidateCallbacks.delete(this.invalidateCallbackFn);
        if (this.objectId !== 0 && this.pickingManager !== undefined) {
            this.pickingManager.removeForId(this.objectId);
            this.pickingManager.removeForObject(this.id);
        }
        Batch3DModel.evictGltf(this);
    }
    styleCallback(style) {
        this.traverse((v) => {
            var _a;
            if (v instanceof Mesh) {
                const material = v.material;
                material.needsUpdate = true;
                material.opacity = (_a = style.opacity) !== null && _a !== void 0 ? _a : 1;
                if (material.opacity < 1) {
                    material.transparent = true;
                    material.depthWrite = false;
                }
                else {
                    material.transparent = false;
                    material.depthWrite = true;
                }
            }
        });
    }
    clippingPlaneCallback(planes) {
        this.traverse((v) => {
            if (v instanceof Mesh) {
                const material = v.material;
                material.clippingPlanes = planes;
                material.needsUpdate = true;
            }
        });
    }
    invalidateCallback() {
        this.traverse((v) => {
            if (v instanceof Mesh) {
                const material = v.material;
                material.needsUpdate = true;
            }
        });
    }
}
function readGltfSection(view) {
    // Skip past all tables and the header to get a data view of just the gltf
    const ftJsonLen = view.getUint32(12, true);
    const ftBinLen = view.getUint32(16, true);
    const btJsonLen = view.getUint32(20, true);
    const btBinLen = view.getUint32(24, true);
    const dataOffset = 32 + ftJsonLen + ftBinLen + btJsonLen + btBinLen;
    // Create a slice to the feature table binary data
    return new DataView(view.buffer, dataOffset, view.byteLength - dataOffset);
}
